import type { Aggregate } from '@cognite/sdk';

/**
 * Field-resolution order for {@link extractDatapointValue} when the caller
 * doesn't know which aggregate populated the datapoint. Mirrors CDF's
 * per-aggregate response shape — `Datapoint.value` for raw datapoints and
 * `DatapointAggregate.{average, sum, count, min, max}` for aggregates. CDF
 * only fills in the field matching the requested aggregate, so the priority
 * order is observable only on hand-built objects: `value` first preserves
 * raw-datapoint semantics; `average` next is the most common aggregate.
 */
export const DATAPOINT_VALUE_FIELDS = ['value', 'average', 'sum', 'count', 'min', 'max'] as const;

/**
 * Extract a finite numeric value from a single datapoint object.
 *
 * - Numbers pass through unchanged (so chained calls like
 *   `value(first(values(arr)))` stay forgiving).
 * - When `aggregate` is supplied, that field is preferred — falling through
 *   to {@link DATAPOINT_VALUE_FIELDS} priority order if missing. Existing
 *   call sites that know the aggregate (e.g. chart-format conversion) get
 *   the targeted lookup; callers that don't (e.g. derive-expression
 *   helpers) get the heuristic walk.
 * - Non-finite numbers (`NaN`, `±Infinity`) collapse to `null` so downstream
 *   math.js operations don't propagate noise.
 *
 * @param dp - A `Datapoint`, `DatapointAggregate`, or raw number; anything
 *   else returns `null`.
 * @param aggregate - Optional preferred aggregate field name.
 * @returns The first finite numeric field present, or `null`.
 */
export function extractDatapointValue(dp: unknown, aggregate?: Aggregate): number | null {
  if (typeof dp === 'number') {
    return Number.isFinite(dp) ? dp : null;
  }
  if (dp === null || typeof dp !== 'object') {
    return null;
  }
  const obj = dp as Record<string, unknown>;

  if (aggregate !== undefined) {
    const v = obj[aggregate];
    if (typeof v === 'number' && Number.isFinite(v)) {
      return v;
    }
  }

  for (const key of DATAPOINT_VALUE_FIELDS) {
    const v = obj[key];
    if (typeof v === 'number' && Number.isFinite(v)) {
      return v;
    }
  }
  return null;
}

/**
 * Extract the (timestamp, value) pair from a single datapoint when both
 * fields are usable. Returns `null` for any other shape so callers can
 * skip cleanly without nesting a chain of guards.
 *
 * The numeric field is resolved via {@link extractDatapointValue} — the
 * optional `aggregate` arg is forwarded for callers that know which field
 * to prefer (e.g. chart-format conversion); callers that don't get the
 * heuristic walk.
 *
 * Used by:
 * - `clientSideBucket` in `engine/timeseries-prefetch.ts` for math.js
 *   bucket fetchers like `monthly(datapoints(ref))`.
 * - `isDatapointArraySamples` in `lib/timeseries/datapoint-detection.ts`
 *   to classify a column as a datapoint-array column (used by the
 *   spread/unroll guards in the Arquero executor).
 * - `expandTimeseries` in `engine/expand-timeseries.ts` for reshaping
 *   datapoint-array columns into wide-format chart-ready datasets.
 */
export function asTimestampedValue(dp: unknown, aggregate?: Aggregate): { ts: Date; value: number } | null {
  if (dp === null || typeof dp !== 'object') {
    return null;
  }
  const ts = (dp as { timestamp?: unknown }).timestamp;
  if (!(ts instanceof Date)) {
    return null;
  }
  const value = extractDatapointValue(dp, aggregate);
  if (value === null) {
    return null;
  }
  return { ts, value };
}
